import { useCallback } from 'react'

import { ResidentAppError } from '../../../common/error'

export const useOpenFile = (
  getSignedUrl: () => Promise<string | undefined>
) => {
  const openFile = useCallback(async (): Promise<void> => {
    const newTab = window.open('about:blank', '_blank')
    if (!newTab) {
      throw ResidentAppError.ClientError('FILE_FAILED_TO_OPEN', {
        message: 'Failed to open new tab (popup blocker).',
      })
    }

    try {
      newTab.document.title = 'Loading…'
      newTab.document.body.innerHTML = '<p>Loading document…</p>'
    } catch {
      /* noop */
    }

    const LOAD_TIMEOUT_MS = 8000
    const loadingTimeoutId = window.setTimeout(() => {
      try {
        newTab.document.body.innerHTML =
          '<p>Loading is taking longer than usual…</p>'
      } catch {
        /* noop */
      }
    }, LOAD_TIMEOUT_MS)

    try {
      const signedUrl = await getSignedUrl()
      if (!signedUrl) {
        throw ResidentAppError.ClientError('FILE_FAILED_TO_LOAD', {
          message: 'Signed URL not received from server.',
        })
      }

      // Omit credentials if different origin (avoid CORS errors with presigned URLs)
      let creds: RequestCredentials = 'include'
      try {
        const su = new URL(signedUrl)
        const here = new URL(window.location.href)
        if (su.origin !== here.origin) creds = 'omit'
      } catch {
        /* noop */
      }

      const res = await fetch(signedUrl, {
        mode: 'cors',
        credentials: creds,
      })
      if (!res.ok) {
        throw ResidentAppError.ClientError('FILE_FAILED_TO_LOAD', {
          message: `Failed to load file (HTTP ${res.status}).`,
        })
      }

      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)

      // Navigate to the new tab
      newTab.location.replace(objectUrl)

      // Cleanup: onload + fallback delay + unload
      newTab.onload = () => {
        try {
          URL.revokeObjectURL(objectUrl)
        } catch {
          /* noop */
        }
      }
      const revokeTimer = window.setTimeout(() => {
        try {
          URL.revokeObjectURL(objectUrl)
        } catch {
          /* noop */
        }
      }, 15000)
      newTab.addEventListener(
        'unload',
        () => {
          try {
            URL.revokeObjectURL(objectUrl)
            window.clearTimeout(revokeTimer)
          } catch {
            /* noop */
          }
        },
        { once: true }
      )
    } catch (err) {
      try {
        newTab.document.title = 'Error'
        newTab.document.body.innerHTML = '<p>Failed to open the file.</p>'
      } catch {
        /* noop */
      }
      throw err
    } finally {
      window.clearTimeout(loadingTimeoutId)
    }
  }, [getSignedUrl])

  return { openFile }
}
